import axios from "axios";
import { isAuthenticated, getToken } from "./auth";
import { apiUrl } from "./apiConfig";

/**
 * Lightweight, fire-and-forget client-side audit tracker.
 *
 * The Express middleware already logs every API call the backend handles;
 * this helper is for events the backend can't directly observe — page
 * views, route changes, hard refreshes, file downloads, key UI clicks.
 *
 * It deliberately uses a bare axios instance (not the shared API client)
 * to avoid recursive auditing and to keep the shipping path short.
 */

type AuditPayload = {
 action: string;
 entityType?: string;
 entityId?: string | number;
 method?: string;
 path?: string;
 statusCode?: number;
 metadata?: Record<string, unknown>;
};

const ENDPOINT = apiUrl("/api/audit/event");

const inflight = axios.create({ baseURL: ""});

const tokenHeader = (): Record<string, string> => {
 if (typeof window ==="undefined") return {};
 const token = getToken();
 return token ? { Authorization:`Bearer ${token}`} : {};
};

export const trackEvent = (event: AuditPayload): void => {

 if (typeof window ==="undefined") return;

 // Skip if there's no valid auth token — anonymous page views before login
 // are noisy and the backend rejects expired tokens with 401 anyway.
 if (!isAuthenticated()) return;

 const payload = {
 ...event,
 path: event.path ?? window.location.pathname + window.location.search,
 metadata: {
 ...(event.metadata ?? {}),
 url: window.location.href,
 referer: document.referrer || null
 }
 };

 // Prefer sendBeacon when the page is unloading so refresh / close
 // events still get a final log. We can't include auth headers with
 // sendBeacon, so we only use it as a last-ditch fallback.
 if (event.action ==="PAGE_UNLOAD"&& navigator.sendBeacon) {
 try {
 const blob = new Blob([JSON.stringify(payload)], {
 type: "application/json"});
 navigator.sendBeacon(ENDPOINT, blob);
 return;
 } catch {
 /* fall through to axios */
 }
 }

 inflight
 .post(ENDPOINT, payload, {
 headers: {"Content-Type":"application/json", ...tokenHeader() },
 timeout: 5000
 })
 .catch(() => {
 /* swallow — never let audit failures break the UI */
 });

};

/**
 * Bind global listeners that catch refreshes, route changes, and file
 * downloads. Call once at app boot from inside <App> after the router
 * mounts.
 */
export const installPageTracker = () => {

 if (typeof window ==="undefined") return;

 // Stamp a flag on the window object so the listeners are attached at
 // most once even if installPageTracker is called twice (HMR, etc.).
 interface AuditFlaggedWindow extends Window {
 __AUDIT_TRACKER_INSTALLED__?: boolean;
 }
 const flaggedWindow = window as AuditFlaggedWindow;

 if (flaggedWindow.__AUDIT_TRACKER_INSTALLED__) return;
 flaggedWindow.__AUDIT_TRACKER_INSTALLED__ = true;

 // Initial page-load (covers hard refresh)
 trackEvent({
 action: "PAGE_VIEW",
 metadata: { trigger: "load"}
 });

 // SPA navigations: react-router uses pushState/replaceState which
 // don't dispatch popstate. Patch them so we can listen.
 const origPush = window.history.pushState;
 const origReplace = window.history.replaceState;

 const fire = (trigger: string) =>
 trackEvent({ action: "PAGE_VIEW", metadata: { trigger } });

 type HistoryStateArgs = Parameters<typeof window.history.pushState>;

 window.history.pushState = function (
 this: History,
 ...args: HistoryStateArgs
 ) {
 const ret = origPush.apply(this, args);
 // Defer so the URL is up-to-date when we read it.
 queueMicrotask(() => fire("pushstate"));
 return ret;
 };

 window.history.replaceState = function (
 this: History,
 ...args: HistoryStateArgs
 ) {
 const ret = origReplace.apply(this, args);
 queueMicrotask(() => fire("replacestate"));
 return ret;
 };

 window.addEventListener("popstate", () => fire("popstate"));

 // Tab close / hard refresh
 window.addEventListener("beforeunload", () => {
 trackEvent({
 action: "PAGE_UNLOAD",
 metadata: { trigger: "beforeunload"}
 });
 });

 window.addEventListener("visibilitychange", () => {
 if (document.visibilityState ==="hidden") {
 trackEvent({
 action: "PAGE_HIDDEN",
 metadata: { trigger: "visibilitychange"}
 });
 }
 });

};

/**
 * Convenience wrapper for"user downloaded a file"events. Use from
 * Excel-export buttons, etc.
 */
export const trackDownload = (
 fileName: string,
 metadata?: Record<string, unknown>
) => {
 trackEvent({
 action: "FILE_DOWNLOAD",
 entityType: "file",
 entityId: fileName,
 metadata: { fileName, ...(metadata ?? {}) }
 });
};
